import { useState, useContext } from "react";
import { FlexTable, Button, useOpenClose, Modal } from "@evertrue/et-components";
import EverTrue from "app";
import TasksToolbar from "./tasks-toolbar";
import DeleteModal from "./delete-modal";
import {
  daysFromNow,
  isPastDate,
  formattedDate,
  getEmptyStateMessageText,
  getEmptyStateMessageSubtext,
} from "apps/tasks/utils/utils";
import Avatar from "components/elements/avatar";
import TaskForm from "../create/task-form";
import useTasks from "apps/tasks/hooks/use-tasks";
import { PropTypes } from "base/react-libs";
import { TasksContext } from "apps/tasks/hooks/tasks-context";
import CompleteTaskToggle from "../complete-task-toggle";
import AddTaskButton from "../create/add-task-button";
import StrikeThroughWrapper from "components/containers/strikethrough-wrapper";
import { useTasksNotificationContext } from "apps/tasks/hooks/tasks-notification-context.js";
import { useIdentity } from "base/identity-resolver";

const TasksTable = ({ tasks = [], loading, total, tab = "", userType, taskFilters, contact }) => {
  const { getAndSetGlobalTasksDueCount } = useTasksNotificationContext();
  const { user } = useIdentity();
  const { deleteTask, toggleCompletionStatus } = useTasks();
  const [selectedTask, setSelectedTask] = useState(null);
  const [isOpen, openModal, closeModal] = useOpenClose();

  const [isTaskModalOpen, setTaskModalOpen] = useState(false);
  const { referrer } = useContext(TasksContext);

  const columns = [
    {
      key: 1,
      headerStyle: { flexGrow: 1 },
      header: "",
      cellClassName: "task-icon-col",
      accessor: (data) => {
        if (data.owner_user_id === user.id) return <CompleteTaskToggle task={data} onClick={toggleCompletionStatus} />;
        return <CompleteTaskToggle task={data} isClickable={false} />;
      },
    },
    {
      key: 2,
      width: 260,
      header: "Task Name",
      accessor: (data) => {
        return (
          <StrikeThroughWrapper hasStrikeThrough={!!data.completed_at}>
            <a
              className="dynamic-lists-table--link fs-exclude-data"
              title={`Open task ${data.title}`}
              onClick={() => {
                setSelectedTask(data);
                isTaskModalOpen ? setTaskModalOpen(false) : setTaskModalOpen(true);
              }}
            >
              {data.title}
            </a>
          </StrikeThroughWrapper>
        );
      },
    },
    {
      key: 3,
      width: 200,
      header: "Constituent",
      accessor: ({ target_info, target_contact_id, completed_at }) => (
        <StrikeThroughWrapper hasStrikeThrough={!!completed_at}>
          {target_contact_id && target_info ? (
            <span style={{ display: "flex", alignItems: "center" }}>
              <Avatar
                className="new-contact-card--avatar fs-exclude-data"
                name={target_info.contact_attributes.name_full}
                url={target_info.profile_image}
                width={40}
              />
              <Button
                className="dynamic-lists-table--link table-toolbar-amount fs-exclude-data"
                type="simple"
                onClick={() => {
                  EverTrue.Navigator(`/contact/${target_contact_id}`, true);
                }}
              >
                {target_info.contact_attributes.name_full}
              </Button>
            </span>
          ) : (
            <span>-</span>
          )}
        </StrikeThroughWrapper>
      ),
    },
    {
      key: 4,
      header: "Due Date",
      accessor: ({ due_date, completed_at }) => {
        if (due_date) {
          const due = daysFromNow(due_date);
          return (
            <StrikeThroughWrapper hasStrikeThrough={!!completed_at}>
              <span className="tasks--due-date">
                <p style={{ color: isPastDate(due_date) && !completed_at ? "red" : "" }}>{due.date}</p>
                {!completed_at && due.msg && <p style={{ color: "red" }}>{due.msg}</p>}
              </span>
            </StrikeThroughWrapper>
          );
        } else return <span>-</span>;
      },
    },
    {
      key: 5,
      header: "Action Type",
      accessor: ({ action_type, completed_at }) => (
        <StrikeThroughWrapper hasStrikeThrough={!!completed_at}>
          {action_type ? (
            <span className="fs-exclude-data">{action_type[0] + action_type.slice(1).toLowerCase()}</span>
          ) : null}
        </StrikeThroughWrapper>
      ),
    },
    {
      key: 6,
      header: "Assignee",
      accessor: ({ owner_info, completed_at }) => (
        <StrikeThroughWrapper hasStrikeThrough={!!completed_at}>
          {owner_info ? owner_info.name : null}
        </StrikeThroughWrapper>
      ),
    },
    {
      key: 7,
      header: "Created By",
      accessor: ({ completed_at, creator_info }) => (
        <StrikeThroughWrapper hasStrikeThrough={!!completed_at}>
          {creator_info ? <span className="fs-exclude-data">{creator_info.name}</span> : null}
        </StrikeThroughWrapper>
      ),
    },
    {
      key: 8,
      header: "Completed At",
      accessor: (data) => (data.completed_at ? formattedDate(data.completed_at) : null),
    },
    {
      key: 9,
      headerStyle: { flexGrow: 1 },
      header: "",
      cellClassName: "task-icon-col",
      accessor: (data) => {
        return (
          <span
            className="trash-btn"
            onClick={() => {
              setSelectedTask(data);
              isOpen ? closeModal() : openModal();
            }}
          ></span>
        );
      },
    },
  ];

  return (
    <>
      <div className="dynamic-lists-controller">
        <TasksToolbar numTasks={total} userType={userType} />
        <div className="tasks-lists-table">
          <FlexTable
            caption="Lists"
            emptyStateMessageText={getEmptyStateMessageText(userType, taskFilters.status)}
            emptyStateMessageSubtext={getEmptyStateMessageSubtext(userType, taskFilters.status)}
            emptyStateActionButton={
              taskFilters.status !== "COMPLETED" && (
                <AddTaskButton primary={true} userType={userType} contact={!userType ? contact : null} />
              )
            }
            columns={
              referrer === "contact"
                ? columns.filter((col) => col.header !== "Constituent")
                : tab === "assigned"
                ? columns.filter((col) => col.header !== "Created By")
                : tab !== "assigned"
                ? columns.filter((col) => col.header !== "Assignee")
                : columns
            }
            data={tasks.slice(0, 50)}
            loading={loading}
          />
        </div>
      </div>
      <DeleteModal
        title="Delete Task"
        body="Are you sure you want to delete this task?"
        isOpen={isOpen}
        onClose={closeModal}
        onSubmit={async () => {
          await deleteTask(selectedTask, userType);
          getAndSetGlobalTasksDueCount();
        }}
      />
      <>
        <Modal
          isOpen={isTaskModalOpen}
          closeModal={() => {
            setTaskModalOpen(false);
          }}
        >
          <TaskForm
            handleClose={() => {
              setTaskModalOpen(false);
            }}
            taskObj={selectedTask}
            contactId={selectedTask && selectedTask.target_contact_id}
          />
        </Modal>
      </>
    </>
  );
};

TasksTable.propTypes = {
  loading: PropTypes.bool,
  tasks: PropTypes.array,
  total: PropTypes.number,
  tab: PropTypes.string,
  userType: PropTypes.string,
  taskFilters: PropTypes.object,
  contact: PropTypes.object,
};

export default TasksTable;
